import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

/**
 * Routage de paiement TAMA (chantier « Routage de paiement »).
 *
 * Deux routes étanches :
 *  - Route TAMA  : Stripe encaisse pour TAMA (Cocos, boosts, Academy, abonnement Pro).
 *  - Route Pro   : virement direct acheteur -> vendeur Pro. TAMA n'encaisse rien.
 *
 * Les valeurs sont lues dans `app_settings` pour rester pilotables depuis l'admin
 * sans redéploiement.
 */
export type CheckoutMode = "beta_dual" | "tama_only" | "pro_only";
export type TamaCatalogPay = "coco" | "card";
export type ProCatalogPay = "transfer" | "disabled";

export interface CheckoutRouting {
  checkoutMode: CheckoutMode;
  tamaCatalogPay: TamaCatalogPay;
  proCatalogPay: ProCatalogPay;
  xpfPerEur: number;
  proSubscriptionAmountXpf: number;
  proSubscriptionInterval: "month" | "year";
}

export const DEFAULT_CHECKOUT_ROUTING: CheckoutRouting = {
  checkoutMode: "beta_dual",
  tamaCatalogPay: "coco",
  proCatalogPay: "transfer",
  xpfPerEur: 119.333,
  proSubscriptionAmountXpf: 9990,
  proSubscriptionInterval: "month",
};

const SETTING_KEYS = [
  "checkout_mode",
  "tama_catalog_pay",
  "pro_catalog_pay",
  "xpf_per_eur",
  "pro_subscription_amount_xpf",
  "pro_subscription_interval",
] as const;

/** Les valeurs sont stockées en JSONB : une chaîne JSON reste une chaîne côté client. */
const readString = <T extends string>(raw: unknown, fallback: T): T =>
  typeof raw === "string" && raw.length > 0 ? (raw as T) : fallback;

const readNumber = (raw: unknown, fallback: number): number => {
  const parsed = Number(raw);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
};

export const CHECKOUT_ROUTING_QUERY_KEY = ["checkout-routing"] as const;

/** Réglages partagés par toutes les cartes d'une page (une requête, 60 s). */
export const useCheckoutRouting = () => {
  const { data, isLoading } = useQuery({
    queryKey: CHECKOUT_ROUTING_QUERY_KEY,
    staleTime: 60_000,
    queryFn: async (): Promise<CheckoutRouting> => {
      const { data, error } = await supabase
        .from("app_settings")
        .select("key, value")
        .in("key", SETTING_KEYS as unknown as string[]);
      if (error) console.error("Lecture du routage de paiement impossible:", error);
      if (!data) return DEFAULT_CHECKOUT_ROUTING;
      const map = new Map(data.map((row) => [row.key, row.value as unknown]));
      return {
        checkoutMode: readString(map.get("checkout_mode"), DEFAULT_CHECKOUT_ROUTING.checkoutMode),
        tamaCatalogPay: readString(map.get("tama_catalog_pay"), DEFAULT_CHECKOUT_ROUTING.tamaCatalogPay),
        proCatalogPay: readString(map.get("pro_catalog_pay"), DEFAULT_CHECKOUT_ROUTING.proCatalogPay),
        xpfPerEur: readNumber(map.get("xpf_per_eur"), DEFAULT_CHECKOUT_ROUTING.xpfPerEur),
        proSubscriptionAmountXpf: readNumber(
          map.get("pro_subscription_amount_xpf"),
          DEFAULT_CHECKOUT_ROUTING.proSubscriptionAmountXpf,
        ),
        proSubscriptionInterval: readString(
          map.get("pro_subscription_interval"),
          DEFAULT_CHECKOUT_ROUTING.proSubscriptionInterval,
        ),
      };
    },
  });
  return { routing: data ?? DEFAULT_CHECKOUT_ROUTING, loading: isLoading };
};
